import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Table,
  Tag,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Modal,
  Alert,
  Popconfirm,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined, CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import Cookies from "js-cookie";

const API = process.env.REACT_APP_BASE_URL;
const SITE = "https://transferven.com";

const authHeaders = () => ({
  headers: { "Content-Type": "application/json", token: Cookies.get("token") },
});

const eur = (n) =>
  `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "2-digit" })
    : "—";

const COMMISSION_TYPES = [
  { value: "percent_of_fee", label: "% de nuestra comisión por orden" },
  { value: "percent_of_volume", label: "% del importe enviado" },
  { value: "fixed_per_order", label: "Fijo (€) por orden completada" },
];

const describeCommission = (a) => {
  const v = Number(a.commissionValue) || 0;
  if (a.commissionType === "percent_of_volume") return `${v}% del importe`;
  if (a.commissionType === "fixed_per_order") return `${eur(v)} por orden`;
  return `${v}% de la comisión`;
};

const statusColor = (s) =>
  ({ Completada: "green", Anulada: "red", Pendiente: "gold", Recibido: "blue", "En Proceso": "blue" }[s] || "default");

const label = { fontSize: 12, color: "#667085", marginBottom: 4 };
const EMPTY = { name: "", email: "", phone: "", notes: "", commissionType: "percent_of_fee", commissionValue: 0 };

const copy = async (text, ok = "Copiado") => {
  try {
    await navigator.clipboard.writeText(text);
    message.success(ok);
  } catch (_) {
    message.info(text);
  }
};

const Agents = () => {
  const { Title, Text } = Typography;

  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // create / edit
  const [form, setForm] = useState(null); // null = closed, {_id?} = open
  const [created, setCreated] = useState(null); // freshly created agent (shows the code)

  // report
  const [report, setReport] = useState(null);
  const [reportAgent, setReportAgent] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [reportLoading, setReportLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/agents`, authHeaders());
      setAgents(data.agents || []);
    } catch (err) {
      message.error(err?.response?.data?.msg || "No se pudieron cargar los agentes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /* ---------------- create / edit ---------------- */

  const save = async () => {
    try {
      setSaving(true);
      if (form._id) {
        await axios.put(`${API}/api/agents/${form._id}`, form, authHeaders());
        message.success("Agente actualizado");
        setForm(null);
      } else {
        const { data } = await axios.post(`${API}/api/agents`, form, authHeaders());
        setForm(null);
        setCreated(data.agent);
      }
      load();
    } catch (err) {
      message.error(err?.response?.data?.msg || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (a, active) => {
    try {
      await axios.put(`${API}/api/agents/${a._id}`, { active }, authHeaders());
      message.success(active ? "Agente activado" : "Agente desactivado: su código ya no funciona para nuevos registros");
      load();
    } catch (err) {
      message.error(err?.response?.data?.msg || "No se pudo actualizar");
    }
  };

  const remove = async (a) => {
    try {
      await axios.delete(`${API}/api/agents/${a._id}`, authHeaders());
      message.success("Agente eliminado");
      load();
    } catch (err) {
      message.warning(err?.response?.data?.msg || "No se pudo eliminar", 8);
    }
  };

  /* ---------------- report ---------------- */

  const openReport = useCallback(
    async (agent, f = "", t = "") => {
      try {
        setReportLoading(true);
        setReportAgent(agent);
        const params = new URLSearchParams();
        if (f) params.set("from", f);
        if (t) params.set("to", t);
        const { data } = await axios.get(`${API}/api/agents/${agent._id}/report?${params}`, authHeaders());
        setReport(data);
      } catch (err) {
        message.error(err?.response?.data?.msg || "No se pudo cargar el informe");
        setReportAgent(null);
      } finally {
        setReportLoading(false);
      }
    },
    []
  );

  const closeReport = () => {
    setReport(null);
    setReportAgent(null);
    setFrom("");
    setTo("");
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const res = await axios.get(`${API}/api/agents/${reportAgent._id}/export?${params}`, {
        ...authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agente-${reportAgent.code}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error("No se pudo exportar");
    }
  };

  const payDue = async () => {
    try {
      const { data } = await axios.post(
        `${API}/api/agents/${reportAgent._id}/pay`,
        { from: from || undefined, to: to || undefined },
        authHeaders()
      );
      message.success(data.msg, 6);
      openReport(reportAgent, from, to);
      load();
    } catch (err) {
      message.error(err?.response?.data?.msg || "No se pudo marcar como pagado");
    }
  };

  /* ---------------- tables ---------------- */

  const columns = [
    {
      title: "Código",
      dataIndex: "code",
      width: 120,
      render: (c, a) => (
        <Tag
          color={a.active ? "red" : "default"}
          style={{ fontSize: 15, padding: "2px 10px", cursor: "pointer", letterSpacing: 2 }}
          onClick={() => copy(c, "Código copiado")}
          title="Copiar"
        >
          {c}
        </Tag>
      ),
    },
    {
      title: "Agente",
      render: (_, a) => (
        <div>
          <div style={{ fontWeight: 600 }}>{a.name}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {a.email}
          </Text>
        </div>
      ),
    },
    { title: "Clientes", dataIndex: "customers", width: 90, align: "center" },
    { title: "Órdenes", dataIndex: "orders", width: 90, align: "center" },
    { title: "Completadas", dataIndex: "completed", width: 110, align: "center" },
    { title: "Volumen", dataIndex: "volume", width: 120, render: eur },
    {
      title: "Comisión",
      width: 190,
      render: (_, a) => <Text type="secondary">{describeCommission(a)}</Text>,
    },
    {
      title: "Pendiente de pago",
      dataIndex: "commissionDue",
      width: 150,
      render: (v) => (
        <b style={{ color: v > 0 ? "#b54708" : "#667085" }}>{eur(v)}</b>
      ),
    },
    {
      title: "Activo",
      width: 80,
      render: (_, a) => <Switch size="small" checked={a.active} onChange={(v) => toggleActive(a, v)} />,
    },
    {
      title: "Acciones",
      width: 250,
      render: (_, a) => (
        <>
          <Button size="small" type="primary" style={{ marginRight: 6 }} onClick={() => openReport(a)}>
            Informe
          </Button>
          <Button size="small" style={{ marginRight: 6 }} onClick={() => setForm({ ...a })}>
            Editar
          </Button>
          <Popconfirm
            title="¿Eliminar este agente?"
            description="Solo se puede si no tiene clientes ni órdenes."
            okText="Eliminar"
            cancelText="Cancelar"
            onConfirm={() => remove(a)}
          >
            <Button size="small" danger>
              Eliminar
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  const orderColumns = [
    { title: "Ref.", dataIndex: "reference", width: 90 },
    { title: "Fecha", dataIndex: "date", width: 90, render: fmtDate },
    {
      title: "Cliente",
      render: (_, r) => (
        <div>
          <div>{r.customer}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            → {r.beneficiary}
          </Text>
        </div>
      ),
    },
    { title: "Estado", dataIndex: "status", width: 110, render: (s) => <Tag color={statusColor(s)}>{String(s).toUpperCase()}</Tag> },
    { title: "Importe", dataIndex: "amount", width: 100, render: eur },
    {
      title: "Comisión agente",
      width: 150,
      render: (_, r) =>
        r.earns ? (
          <span>
            <b>{eur(r.commission)}</b>{" "}
            {r.paid ? <Tag color="green">pagada</Tag> : <Tag color="orange">pendiente</Tag>}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  const t = report?.totals;
  const linkFor = (a) => `${SITE}/registro?ref=${a.code}`;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <Title level={window.matchMedia("(max-width: 600px)").matches ? 4 : 3} style={{ margin: 0 }}>
          Agentes y comisiones
        </Title>
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Actualizar
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setForm({ ...EMPTY })}>
            Nuevo agente
          </Button>
        </div>
      </div>

      {/* <Alert
        style={{ margin: "16px 0" }}
        type="info"
        showIcon
        message="Los agentes no tienen acceso a la plataforma"
        description="Cada agente comparte su código de 4 dígitos. Los clientes lo escriben (opcional) al registrarse y todas sus órdenes quedan vinculadas al agente. La comisión se calcula sobre las órdenes COMPLETADAS."
      /> */}

      <Table
        size="middle"
        rowKey="_id"
        loading={loading}
        dataSource={agents}
        columns={columns}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: "Todavía no hay agentes. Crea el primero con «Nuevo agente»." }}
      />

      {/* ---------------- crear / editar ---------------- */}
      <Modal
        open={!!form}
        title={form?._id ? `Editar agente · ${form.code}` : "Nuevo agente"}
        onCancel={() => setForm(null)}
        onOk={save}
        okText={form?._id ? "Guardar" : "Crear agente"}
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
      >
        {form && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 12 }}>
            <div>
              <div style={label}>Nombre</div>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre y apellido" />
            </div>
            <div>
              <div style={label}>Email</div>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="agente@email.com" />
            </div>
            <div>
              <div style={label}>Teléfono (opcional)</div>
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <div style={label}>Cómo se calcula su comisión</div>
              <Select
                style={{ width: "100%" }}
                value={form.commissionType}
                onChange={(v) => setForm({ ...form, commissionType: v })}
                options={COMMISSION_TYPES}
              />
            </div>
            <div>
              <div style={label}>{form.commissionType === "fixed_per_order" ? "Importe fijo (€)" : "Porcentaje (%)"}</div>
              <InputNumber
                style={{ width: 160 }}
                min={0}
                step={form.commissionType === "fixed_per_order" ? 0.5 : 1}
                value={form.commissionValue}
                onChange={(v) => setForm({ ...form, commissionValue: v ?? 0 })}
              />
            </div>
            <div>
              <div style={label}>Notas internas (opcional)</div>
              <Input.TextArea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {!form._id && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                El código de 4 dígitos se genera automáticamente al crear el agente.
              </Text>
            )}
          </div>
        )}
      </Modal>

      {/* ---------------- agente creado ---------------- */}
      <Modal
        open={!!created}
        title="Agente creado"
        onCancel={() => setCreated(null)}
        footer={<Button type="primary" onClick={() => setCreated(null)}>Listo</Button>}
      >
        {created && (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <Text type="secondary">Código de {created.name}</Text>
            <div style={{ fontSize: 44, fontWeight: 700, letterSpacing: 8, color: "#F6394F", margin: "6px 0 14px" }}>
              {created.code}
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <Button icon={<CopyOutlined />} onClick={() => copy(created.code, "Código copiado")}>
                Copiar código
              </Button>
              <Button icon={<CopyOutlined />} onClick={() => copy(linkFor(created), "Enlace copiado")}>
                Copiar enlace de registro
              </Button>
            </div>
            <Text type="secondary" style={{ display: "block", marginTop: 14, fontSize: 12 }}>
              El enlace abre el registro con el código ya escrito.
            </Text>
          </div>
        )}
      </Modal>

      {/* ---------------- informe ---------------- */}
      <Modal
        open={!!reportAgent}
        title={reportAgent ? `${reportAgent.name} · código ${reportAgent.code}` : ""}
        onCancel={closeReport}
        footer={null}
        width={920}
        destroyOnClose
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
          <div>
            <div style={label}>Desde</div>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <div style={label}>Hasta</div>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <Button onClick={() => openReport(reportAgent, from, to)} loading={reportLoading}>
            Filtrar
          </Button>
          <Button
            onClick={() => {
              setFrom("");
              setTo("");
              openReport(reportAgent);
            }}
          >
            Todo
          </Button>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Button icon={<DownloadOutlined />} onClick={exportCsv}>
              Exportar CSV
            </Button>
            <Popconfirm
              title="¿Marcar como pagadas las comisiones pendientes?"
              description={`Se marcarán ${t ? eur(t.commissionDue) : ""} (dentro del rango de fechas elegido).`}
              okText="Sí, ya las pagué"
              cancelText="Cancelar"
              onConfirm={payDue}
              disabled={!t || t.commissionDue <= 0}
            >
              <Button type="primary" disabled={!t || t.commissionDue <= 0}>
                Marcar pagadas
              </Button>
            </Popconfirm>
          </div>
        </div>

        {t && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 16 }}>
            {[
              ["Clientes", report.customers.length],
              ["Órdenes", t.orders],
              ["Completadas", t.completed],
              ["Volumen completado", eur(t.volume)],
              ["Pendiente de pago", eur(t.commissionDue), "#b54708"],
              ["Ya pagado", eur(t.commissionPaid), "#027a48"],
            ].map(([k, v, c]) => (
              <div key={k} style={{ border: "1px solid #eaecf0", borderRadius: 10, padding: "10px 14px" }}>
                <div style={{ fontSize: 11, color: "#667085" }}>{k}</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: c || "#101828" }}>{v}</div>
              </div>
            ))}
          </div>
        )}

        <Table
          size="small"
          rowKey="reference"
          loading={reportLoading}
          dataSource={report?.orders || []}
          columns={orderColumns}
          pagination={{ pageSize: 8, showSizeChanger: false }}
          scroll={{ x: 640 }}
          locale={{ emptyText: "Este agente todavía no tiene órdenes en este periodo" }}
        />

        {report?.customers?.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Clientes registrados con este código:{" "}
              {report.customers.map((c) => `${c.name || ""} ${c.surname || ""}`.trim() || c.email).join(", ")}
            </Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Agents;
